import { useEffect } from 'react'

import logger from '../../../utils/logger'
import { GoogleMapsAPIUrl } from '../constants'

import { ScriptStatus, useScript } from './useScript'

export enum GoogleMapsLibrary {
  Geocoding = 'geocoding',
  Geometry = 'geometry',
  Places = 'places',
}

const googleMapsLibraryCheckers: Record<GoogleMapsLibrary, () => boolean> = {
  [GoogleMapsLibrary.Geocoding]: () => !!window.google?.maps?.Geocoder,
  [GoogleMapsLibrary.Geometry]: () => !!window.google?.maps?.geometry,
  [GoogleMapsLibrary.Places]: () => !!window.google?.maps?.places,
}

const noRequiredLibraries: GoogleMapsLibrary[] = []

/** Google Maps is loaded once per page, so the failure is only worth reporting once */
let isUnavailabilityReported = false

/**
 * Loads the Google Maps API script and reports its status.
 *
 * A script which reports as loaded without leaving a usable `window.google`
 * behind (blocked, aborted or hijacked request) is reported as
 * `ScriptStatus.Error` instead of throwing, so a Google Maps failure only
 * degrades map and address features instead of taking down the whole page.
 *
 * @param requiredLibraries Google Maps libraries which have to be available
 * for the script to be considered ready
 */
export default function useGoogleMapsScript(
  requiredLibraries: GoogleMapsLibrary[] = noRequiredLibraries,
) {
  const scriptStatus = useScript(GoogleMapsAPIUrl)

  const requiredLibrariesKey = requiredLibraries.join(',')

  const isGoogleMapsAvailable =
    typeof window !== 'undefined' &&
    !!window.google?.maps &&
    requiredLibraries.every(library => googleMapsLibraryCheckers[library]())

  const status =
    scriptStatus === ScriptStatus.Ready && !isGoogleMapsAvailable
      ? ScriptStatus.Error
      : scriptStatus

  useEffect(() => {
    if (status === ScriptStatus.Error && !isUnavailabilityReported) {
      isUnavailabilityReported = true

      logger.error('Google Maps JavaScript API is not available', {
        additionalData: {
          scriptStatus,
          requiredLibraries: requiredLibrariesKey,
        },
      })
    }
  }, [requiredLibrariesKey, scriptStatus, status])

  return status
}
